import { useEffect, useState } from "react";
import { getCurrentTheme, toggleTheme, type Theme } from "../lib/theme.js";

/**
 * Sun/moon toggle for dark/light mode.
 *
 * Renders a small icon button (24px) — sun when current theme is dark
 * (indicating "switch to light"), moon when current is light (indicating
 * "switch to dark"). The label/aria-label reflects the action, not the
 * current state, so screen readers say "Switch to light theme" etc.
 *
 * Listens for the `bht:theme-changed` event so multiple toggles stay in
 * sync if rendered in different places.
 */
export function ThemeToggle({ size = 22 }: { size?: number }) {
  const [theme, setThemeState] = useState<Theme>(() => getCurrentTheme());

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<Theme>).detail;
      if (detail === "dark" || detail === "light") {
        setThemeState(detail);
      }
    };
    window.addEventListener("bht:theme-changed", handler);
    return () => window.removeEventListener("bht:theme-changed", handler);
  }, []);

  const onClick = () => {
    const next = toggleTheme();
    setThemeState(next);
  };

  const isDark = theme === "dark";
  const actionLabel = isDark ? "Switch to light theme" : "Switch to dark theme";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={actionLabel}
      title={actionLabel}
      // Borderless. A UX review singled this out as carrying more weight than a
      // control changed once a year deserves, and it was right: the outlined box
      // read louder than the chat glyph beside it, which is a far more frequent
      // action. The review's own fix was to move it into settings, which we did
      // NOT do — it lives in the header because cohort feedback said a
      // drawer-buried toggle was easy to miss (see AppHeader). Quieter, not gone.
      style={{
        appearance: "none",
        background: "transparent",
        border: "none",
        borderRadius: 6,
        padding: 4,
        width: Math.max(44, size + 12),
        height: Math.max(44, size + 12),
        cursor: "pointer",
        color: "var(--fg-2)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "color 120ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "var(--fg-0)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "var(--fg-2)";
      }}
    >
      {isDark ? (
        // Sun (offering: switch to light)
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="4" />
          <line x1="12" y1="2" x2="12" y2="4" />
          <line x1="12" y1="20" x2="12" y2="22" />
          <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
          <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
          <line x1="2" y1="12" x2="4" y2="12" />
          <line x1="20" y1="12" x2="22" y2="12" />
          <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
          <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
        </svg>
      ) : (
        // Moon (offering: switch to dark)
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}
